import { useEffect } from 'react';

export interface useErrorFocusProps {
  // An object which has `keys` that represent the field `name` and a string value that describes the error for the field. (Formik)
  errors?: object;

  // Indication of whether or not the form is currently considered as being submitted. The focus will only happen when this is `true`.
  isSubmitting?: boolean;

  /*
   * Explicitly specify the fields eligible for focus if they are found to have an error, and the order in which they should be chosen for focus.
   * i.e. The first field name in this list that matches a key in `errors` will get focused (if isSubmitting is true).
   * If you want/need to restrict the fields that would get focus if they are the first field with an error, you can explicitly indicate them here.
   */
  focusableFieldNames?: Array<string> | null;
}

/**
 * This hook is useful for accessibility in forms, where we should focus on the first field with errors (if any), after an attempt at submitting the form occurs.
 */
export function useErrorFocus(props: useErrorFocusProps) {
  const { errors = {}, isSubmitting = false, focusableFieldNames = null } = props;

  // Disabling the lint rule below is because this is a custom hook.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (isSubmitting && errors) {
      const fieldNamesWithErrors = errors ? Object.keys(errors) : [];
      if (fieldNamesWithErrors?.length) {
        const focusableFieldName = focusableFieldNames?.length
          ? focusableFieldNames.find(name => fieldNamesWithErrors.includes(name))
          : fieldNamesWithErrors[0];
        if (focusableFieldName) {
          const element = document.querySelector(`[name="${focusableFieldName}"`) as HTMLElement;
          element && typeof element.focus === 'function' && element.focus();
        }
      }
    }
  }, [isSubmitting, errors, focusableFieldNames]);
}
